import React from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import TablePager from '../../CrewingAnalytics/components/TablePager';
import type { WesDetailRowView } from '../wesPalletStagingMappers';
import { WES_METRICS, WES_PAGE_SIZE_OPTIONS, WES_TABLE_EMPTY_MESSAGE, effectiveSort, type WesMetricKey, type WesTableSort } from '../wesPalletStagingTable';
import { WesTooltipTarget } from './WesTooltip';

const LOCATION_WIDTH = 120;
const METRIC_WIDTH = 120;
const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 56;
const LOCATION_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

interface WesLocationMobileTableProps {
  // Groups are by Location only on mobile; the dimension picker is not available.
  rows: WesDetailRowView[];
  totals: WesDetailRowView;
  sort: WesTableSort | null;
  page: number;
  totalGroups: number;
  pageSize: number;
  empty: boolean;
  onSort: (key: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

// Location stays fixed on the left while the four metric columns scroll sideways. Fixed row heights keep both sides aligned. Look follows the Crewing table.
const WesLocationMobileTable: React.FC<WesLocationMobileTableProps> = ({ rows, totals, sort, page, totalGroups, pageSize, empty, onSort, onPageChange, onPageSizeChange }) => {
  const active = effectiveSort(sort, ['location']);
  const displayRows = active.key === 'location'
    ? [...rows].sort((a, b) => LOCATION_COLLATOR.compare(a.text.location ?? '', b.text.location ?? '') * (active.dir === 'asc' ? 1 : -1))
    : rows;

  const header = (key: string, label: string, width: number, numeric: boolean, sortable: boolean) => {
    const isActive = sortable && sort !== null && active.key === key;
    const content = (
      <>
        <Text style={[styles.headerText, isActive && styles.headerTextActive, numeric && styles.headerTextNum]} numberOfLines={3}>{label}</Text>
        {sortable ? <Text style={styles.sortMark} testID={`wes-sort-${key}`}>{isActive ? (active.dir === 'asc' ? '▲' : '▼') : ''}</Text> : null}
      </>
    );
    return sortable ? (
      <TouchableOpacity
        key={key}
        style={[styles.headerCell, numeric && styles.headerCellNum, { width }]}
        onPress={() => onSort(key)}
        accessibilityRole="button"
        accessibilityLabel={`Sort by ${label}`}
        testID={`wes-th-${key}`}
      >
        {content}
      </TouchableOpacity>
    ) : (
      <View key={key} style={[styles.headerCell, numeric && styles.headerCellNum, { width }]} testID={`wes-th-${key}`}>
        {content}
      </View>
    );
  };

  const metricCell = (row: WesDetailRowView, key: WesMetricKey, cellTestID: string, tipTestID: string, textStyle: object) => {
    if (key !== 'audit') {
      return (
        <Text key={key} style={[textStyle, styles.num, { width: METRIC_WIDTH }]} testID={cellTestID}>
          {row.metrics[key]}
        </Text>
      );
    }
    return (
      <View key={key} style={{ width: METRIC_WIDTH, alignItems: 'flex-end' }}>
        <WesTooltipTarget text={row.auditTip} testID={tipTestID} placement="left" maxWidth={220}>
          <Text style={[textStyle, styles.num]} testID={cellTestID}>
            {row.metrics[key]}
          </Text>
        </WesTooltipTarget>
      </View>
    );
  };

  return (
    <View>
      <View style={styles.table} testID="wes-mobile-table">
        <View style={styles.frozen} testID="wes-frozen-column">
          {header('location', 'Location', LOCATION_WIDTH, false, true)}
          {empty ? null : (
            <View style={[styles.cellBox, styles.totalBox]}>
              <Text style={styles.totalLabel}>Total</Text>
            </View>
          )}
          {displayRows.map((row, ri) => (
            <View key={row.id} style={styles.cellBox}>
              <WesTooltipTarget
                text={row.text.location ?? ''}
                testID={`wes-location-name-${ri}`}
                style={styles.locationTooltip}
                maxWidth={260}
              >
                <Text style={styles.cell} numberOfLines={1} testID={`wes-cell-${ri}-location`}>{row.text.location}</Text>
              </WesTooltipTarget>
            </View>
          ))}
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator testID="wes-metrics-scroll">
          <View>
            <View style={styles.headerRow}>{WES_METRICS.map((m) => header(m.key, m.label, METRIC_WIDTH, true, true))}</View>
            {empty ? null : (
              <>
                <View style={[styles.cellRow, styles.totalBox]} testID="wes-total-row">
                  {WES_METRICS.map((m) => metricCell(totals, m.key, `wes-total-${m.key}`, 'wes-tip-total-audit', styles.totalText))}
                </View>
                {displayRows.map((row, ri) => (
                  <View key={row.id} style={styles.cellRow} testID={`wes-row-${ri}`}>
                    {WES_METRICS.map((m) => metricCell(row, m.key, `wes-cell-${ri}-${m.key}`, `wes-tip-${ri}-audit`, styles.cell))}
                  </View>
                ))}
              </>
            )}
          </View>
        </ScrollView>
      </View>

      {empty ? (
        <View style={styles.emptyRow} testID="wes-table-empty">
          <Text style={styles.emptyText}>{WES_TABLE_EMPTY_MESSAGE}</Text>
        </View>
      ) : <TablePager page={page} pageSize={pageSize} total={totalGroups} onPageChange={onPageChange} pageSizeOptions={WES_PAGE_SIZE_OPTIONS} onPageSizeChange={onPageSizeChange} hoverEffects />}
    </View>
  );
};

const styles = StyleSheet.create({
  table: { flexDirection: 'row' },
  frozen: { width: LOCATION_WIDTH, borderRightWidth: 1, borderRightColor: '#D7E5F8', backgroundColor: '#FFFFFF' },
  headerRow: { flexDirection: 'row', height: HEADER_HEIGHT, borderBottomWidth: 2, borderBottomColor: '#D7E5F8', backgroundColor: '#FFFFFF' },
  headerCell: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 6,
    paddingVertical: 8,
    height: HEADER_HEIGHT,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
  },
  headerCellNum: { justifyContent: 'flex-end', borderBottomWidth: 0 },
  headerText: { flexShrink: 1, fontSize: 10, fontFamily: FontFamily.bold, fontWeight: '700', color: '#616161', textTransform: 'uppercase', letterSpacing: 0.3 },
  headerTextNum: { textAlign: 'right' },
  headerTextActive: { color: '#034895' },
  sortMark: { fontSize: 10, color: '#034895', marginLeft: 2, minWidth: 10 },
  cellBox: { height: ROW_HEIGHT, justifyContent: 'center', paddingHorizontal: 6, borderBottomWidth: 1, borderBottomColor: '#D7E5F8' },
  locationTooltip: { flex: 1, justifyContent: 'center' },
  cellRow: { flexDirection: 'row', height: ROW_HEIGHT, alignItems: 'center', borderBottomWidth: 1, borderBottomColor: '#D7E5F8' },
  totalBox: { backgroundColor: '#F3F7FD', borderTopWidth: 1, borderTopColor: '#D0D0D0' },
  emptyRow: { minHeight: 64, justifyContent: 'center', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: '#D7E5F8' },
  emptyText: { paddingHorizontal: 12, fontSize: 13, fontFamily: FontFamily.medium, color: '#616161', textAlign: 'center' },
  totalLabel: { fontSize: 12, fontFamily: FontFamily.bold, fontWeight: '700', color: '#2D2D2D' },
  totalText: { paddingHorizontal: 6, fontSize: 12, fontFamily: FontFamily.bold, fontWeight: '700', color: '#2D2D2D' },
  cell: { paddingHorizontal: 6, fontSize: 12, fontFamily: FontFamily.medium, color: '#3A3A3A' },
  num: { textAlign: 'right' },
});

export default WesLocationMobileTable;
